/**
 * Renders a model's narrative as a formatted passage.
 *
 * The parsing lives in lib/report/prose.js; this is only the layout of what
 * comes back, plus the one piece of inline markup the report interprets.
 *
 * Styled from the clinical stylesheet rather than from utility classes: this
 * prose sits between ruled tables in a document that is printed as often as it
 * is read, and a passage set in a different size and colour from the findings
 * above it reads as a different document pasted in.
 */

import React from 'react';
import { parseProse } from '../../lib/report/prose';
import { splitMeasurements, splitSentences, statesFinding } from '../../lib/report/highlight';

/** Keep provider and model branding out of patient-facing report narratives. */
export function sanitizeReportText(value) {
  return String(value ?? '')
    .replace(/\b(?:Claude(?:\s+\d+(?:\.\d+)?)?(?:\s+(?:Sonnet|Opus|Haiku))?|GPT-?\d+(?:\.\d+)?(?:o)?(?:\s+Vision)?|OpenAI|Anthropic)\b/gi, 'assessment system')
    .replace(/\bAI\s+vision\b/gi, 'image analysis')
    .replace(/\bAI\s+analysis\b/gi, 'automated analysis')
    .replace(/\bAI\s+interpretation\b/gi, 'automated interpretation');
}

/**
 * Splits **emphasis** out of a line, and marks the measurements inside what
 * is left. Nothing else is interpreted.
 */
function inline(text, keyPrefix) {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1
      ? <strong key={`${keyPrefix}-${i}`}>{part}</strong>
      : (
        <React.Fragment key={`${keyPrefix}-${i}`}>
          {splitMeasurements(part).map((run, j) =>
            run.key
              ? <b className="key" key={j}>{run.text}</b>
              : <React.Fragment key={j}>{run.text}</React.Fragment>)}
        </React.Fragment>
      )
  );
}

/**
 * A paragraph, with the sentences that state a flagged reading marked.
 *
 * `crux` is the list of out-of-range readings from the findings table. A long
 * passage is where a patient loses the thread, so the lines carrying the
 * findings are lifted out of it — and because the match is on the number, a
 * marked line can never disagree with the table it came from.
 */
function Paragraph({ text, crux, index }) {
  if (!crux?.length) return <p>{inline(text, String(index))}</p>;
  return (
    <p>
      {splitSentences(text).map((sentence, i) =>
        statesFinding(sentence, crux)
          ? <mark className="crux" key={i}>{inline(sentence, `${index}-${i}`)}</mark>
          : <React.Fragment key={i}>{inline(sentence, `${index}-${i}`)}</React.Fragment>)}
    </p>
  );
}

export function Prose({ text, crux }) {
  const blocks = parseProse(sanitizeReportText(text));
  if (!blocks.length) return null;

  return (
    <>
      {blocks.map((block, i) => {
        if (block.type === 'list') {
          return (
            <ul key={i} className="bul">
              {block.items.map((item, j) => <li key={j}>{inline(item, `${i}-${j}`)}</li>)}
            </ul>
          );
        }
        if (block.type === 'heading') {
          return <h3 key={i} className="sub">{block.text}</h3>;
        }
        return <Paragraph key={i} text={block.text} crux={crux} index={i} />;
      })}
    </>
  );
}

export default Prose;
